import {z} from 'zod';
import {ApiDefinition} from '../definitions';
import {ObjectIdSchema} from '../../zod';
import {IntentSourceLabel, WebIntentSourcePath} from '../../validation';
import {AccountFieldOrCustomFieldSchema} from '../../signalDefinition';

export const MaxIntentSources = 50;

const IntentSourceLabelSchema = z
  .string()
  .trim()
  .min(
    IntentSourceLabel.minLength,
    `Label must be at least ${IntentSourceLabel.minLength} characters`
  )
  .max(
    IntentSourceLabel.maxLength,
    `Label must be at most ${IntentSourceLabel.maxLength} characters`
  )
  .regex(IntentSourceLabel.pattern, 'Label contains invalid characters');

const WebIntentSourcePathSchema = z
  .string()
  .trim()
  .min(
    WebIntentSourcePath.minLength,
    `Path must be at least ${WebIntentSourcePath.minLength} characters`
  )
  .max(
    WebIntentSourcePath.maxLength,
    `Path must be at most ${WebIntentSourcePath.maxLength} characters`
  );

export const CreateIntentSource = {
  path: 'intent/createIntentSource',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('api'),
      label: IntentSourceLabelSchema,
    }),
    z.object({
      type: z.literal('webIntent'),
      label: IntentSourceLabelSchema,
      path: WebIntentSourcePathSchema,
    }),
  ]),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const EditIntentSource = {
  path: 'intent/editIntentSource',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    sourceId: ObjectIdSchema,
    label: IntentSourceLabelSchema,
    path: WebIntentSourcePathSchema,
    type: z.literal('webIntent'),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const DeleteIntentSource = {
  path: 'intent/deleteIntentSource',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    sourceId: ObjectIdSchema,
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const CreateIntentModel = {
  path: 'intent/createModel',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: z.object({
    id: ObjectIdSchema,
  }),
} satisfies ApiDefinition;

export const SaveIntentModel = {
  path: 'intent/saveModel',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    id: ObjectIdSchema,
    fieldsWithWeights: z.array(
      z.object({
        field: AccountFieldOrCustomFieldSchema,
        weight: z.number(),
      })
    ),
    thresholds: z
      .object({
        low: z.number().min(0, 'Low threshold must be greater than 0'),
        medium: z.number().min(0, 'Medium threshold must be greater than 0'),
        high: z.number().min(0, 'High threshold must be greater than 0'),
      })
      .refine(
        (thresholds) =>
          thresholds.low < thresholds.medium &&
          thresholds.medium < thresholds.high,
        {
          message:
            'Thresholds must be in increasing order (low < medium < high)',
        }
      ),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;
